// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_INVESTOR_STATEMENTS } from '../../../state_manager/globals.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import type { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'
import { InvestorStatementRunRecordFields } from '../../../state_manager/data_models/investor_statement_run_record.ts'
import type { FilterOptions, FilterConfig } from '../../../core/components/TableView/TableViewTypes.ts'

interface InvestorStatementRunsTableProps {
  data: Record<string, unknown>[]
  loading: boolean
  loadingMore?: boolean
  hasMore?: boolean
  currentPage?: number
  gridColumns: ViewConfigColumn[]
  activeFilters?: FilterValue[]
  activeSorting?: SortConfig[]
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  onLoadMore?: (page: number, pageSize: number) => void | Promise<void>
  onFiltersChange?: (filters: FilterConfig) => void
  onSortChange?: (sortConfig: any) => void
}

export const InvestorStatementRunsTable = React.memo(function InvestorStatementRunsTable({
  data,
  loading,
  loadingMore = false,
  hasMore = false,
  currentPage = 1,
  gridColumns,
  activeFilters = [],
  activeSorting = [],
  filterOptions = {},
  filterOptionsLoading = false,
  onLoadMore,
  onFiltersChange,
  onSortChange
}: InvestorStatementRunsTableProps) {
  const { openFormInTab } = useTabManagerContext()

  const openRecordFullColumns = useMemo(
    () => new Set(gridColumns.filter((c) => c.fieldKey === 'open-record-full').map((c) => c.valueKey)),
    [gridColumns]
  )

  // Related-record links: a column whose cfg carries link_id_field pointing
  // at ANOTHER table's id (e.g. funds__id) opens that record's full form
  // instead of this grid's own record. valueKey -> {idField, table}.
  const relatedLinkByValueKey = useMemo(() => {
    const m = new Map<string, { idField: string; table: string }>()
    for (const c of gridColumns) {
      if (c.fieldKey !== 'open-record-full' || !c.linkIdField) continue
      const match = /^([a-z0-9_]+)__id$/.exec(c.linkIdField)
      if (!match) continue
      const table = match[1].replace(/_/g, '-')
      if (table === 'investor-statement-runs') continue
      m.set(c.valueKey, { idField: c.linkIdField, table })
    }
    return m
  }, [gridColumns])

  const { columns: baseColumns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: gridColumns,
      fallbackRecordModuleKey: 'investor_statements'
    })
  }, [gridColumns])

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const prevRender = col.render
      if (!prevRender) return col
      const opensRecordFull = openRecordFullColumns.has(String(col.key))
      if (!opensRecordFull) return col
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)

          const related = relatedLinkByValueKey.get(String(col.key))
          if (related) {
            const relatedId = record[related.idField]
            if (typeof relatedId !== 'string' || !relatedId) return node
            return (
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => openFormInTab(related.table, relatedId, typeof value === 'string' ? value : undefined)}
              >
                {node}
              </button>
            )
          }

          const runId =
            typeof record[InvestorStatementRunRecordFields.investor_statement_runs__id] === 'string'
              ? (record[InvestorStatementRunRecordFields.investor_statement_runs__id] as string)
              : ''
          if (!runId) return node

          const title =
            typeof record[InvestorStatementRunRecordFields.investor_statement_runs__name] === 'string'
              ? (record[InvestorStatementRunRecordFields.investor_statement_runs__name] as string)
              : ''

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab(TABLE_INVESTOR_STATEMENTS, runId, title || 'Statement Run')}
            >
              {node}
            </button>
          )
        }
      }
    })
  }, [baseColumns, openRecordFullColumns, relatedLinkByValueKey, openFormInTab])

  const dataSource = useMemo(() => {
    const src = data ?? []
    return src.map((row, i) => {
      const idValue = row[InvestorStatementRunRecordFields.investor_statement_runs__id]
      const id = typeof idValue === 'string' && idValue ? idValue : `row-${i}`
      return { ...row, id, key: id }
    })
  }, [data])

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (columns.length === 0) return null

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        onLoadMore={onLoadMore}
        currentFilters={currentFilters}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={currentSortConfig}
        frozenColumns={frozenColumns}
        headerStyle={{ padding: '5px', minHeight: 40, backgroundColor: '#f5f5f5' }}
      />
    </div>
  )
})

export default InvestorStatementRunsTable
